const CONTENT = {
  en: {
    page: {
      discountBadge: "{percent}% OFF",
      eyebrow: "Launch pricing",
      title: "Choose the Jobolar experience built for your next move.",
      description: "Start with a complete free trial, then keep the interview guidance and application workflow that fits the way you search.",
      promoLine: "Use code {code} · all displayed prices already include the discount.",
      billingIntervalAria: "Billing interval",
    },
    billing: {
      monthly: "Monthly",
      quarterly: "Quarterly",
      semiannual: "Semiannual",
      shortMonthly: "mo",
      shortQuarterly: "qr",
      shortSemiannual: "sa",
      termMonthly: "monthly",
      termQuarterly: "quarterly",
      termSemiannual: "semiannual",
    },
    freeTrial: {
      badge: "7-day complete experience",
      headline: "Explore the complete Jobolar experience—no card required.",
      features: [
        "7-day free trial",
        "No payment card required",
        "One shared trial across Jobolar Web, the Chrome extension and Interview Assistant",
        "One trial per Google account",
        "Up to 10 application submissions during the trial, limited to 3 per day",
        "Unlimited deterministic Copilot form filling",
        "5 Generate/Refine with AI drafts",
        "2 Smart Tailor résumés",
        "2 Deep Tailor résumés",
        "15 Live Interview Assistant minutes",
        "15 Mock Interview minutes",
        "Trial expires automatically",
      ],
      startCta: "Start my free trial",
      starting: "Starting…",
      activeUntil: "Trial active until {date}",
      unavailable: "Trial activation is temporarily unavailable",
      alreadyUsed: "Free trial already used",
      footnote: "No payment card · Expires automatically",
    },
    plans: {
      basic: {
        name: "Basic",
        tag: "A polished start",
        description: "Discover matching opportunities, complete application forms faster and prepare for interviews—without automated submissions.",
        featuredFeatures: [
          "60 Live Interview Assistant minutes per billing cycle",
          "90 Mock Interview minutes per billing cycle",
          "Manual Listen on Jobolar Web for live interviews",
          "Connect the Jobolar Chrome extension",
          "Unlimited deterministic Copilot Fill through the Chrome extension",
          "Daily AI job search and matching",
        ],
        features: [
          "Real-time interview-question capture, talking points and suggested answers",
          "Deterministic filling does not consume AI credits",
          "Curated local and international job opportunities",
          "Unlimited job alerts",
          "Direct job links delivered by email",
          "Job-search analytics dashboard",
          "Upgrade to Standard or Pro for Smart Apply, Email Auto Apply, AI-generated application drafts, résumé automation and scheduled submissions",
        ],
      },
      standard: {
        name: "Standard",
        tag: "Most popular · Best value",
        description: "Let Jobolar send eligible email applications with your unchanged Base Resume while Smart Apply helps you complete supported browser applications faster.",
        featuredFeatures: [
          "150 Live Interview Assistant minutes per billing cycle",
          "225 Mock Interview minutes per billing cycle",
          "Manual Listen on Jobolar Web and the Chrome extension",
          "Email Auto Apply using your unchanged Base Resume",
          "Email Auto Apply available in 49 supported countries",
          "Smart Apply and Browser-local Smart Form through the Chrome extension",
          "Scheduled Chrome Auto Apply",
          "Up to 200 confirmed Chrome Auto Apply submissions per billing cycle",
        ],
        features: [
          "Real-time interview-question capture, talking points and suggested answers",
          "Required AI-generated cover letters for eligible email applications",
          "Saved application answers",
          "Base Resume automation",
          "Review Before Submit and supported auto-submission",
          "Required cover-letter generation",
          "Pause, resume and cancel controls",
          "Maximum 20 confirmed Chrome Auto Apply submissions per day",
          "Unlimited deterministic résumé matching",
          "Unlimited deterministic Copilot form filling",
          "Up to 200 Copilot Generate/Refine with AI drafts per billing cycle",
          "Maximum 20 Copilot AI drafts per day",
          "Priority processing",
          "Daily AI job search and matching",
          "Curated opportunities, unlimited alerts, direct links and analytics",
          "Standard uses only the unchanged Base Resume",
          "Smart Tailor and Deep Tailor remain locked",
        ],
      },
      pro: {
        name: "Pro",
        tag: "Complete experience",
        description: "Apply with job-specific AI-tailored résumés and cover letters, unlock complete Smart Apply automation and receive discreet AI guidance during live interviews.",
        featuredFeatures: [
          "300 Live Interview Assistant minutes per billing cycle",
          "450 Mock Interview minutes per billing cycle",
          "Manual and Smart Listen on Jobolar Web, the Chrome extension and Desktop",
          "Screen-share-invisible Desktop Interview Assistant",
          "Email Auto Apply with job-specific AI-tailored résumés",
          "Email Auto Apply available in 49 supported countries",
          "Everything included in Standard Smart Apply",
          "Scheduled Chrome Auto Apply",
        ],
        features: [
          "Real-time question capture, suggested talking points, sample answers, answer structure, insights and possible follow-up questions",
          "Required AI-generated cover letters for eligible email applications",
          "Base Resume",
          "Smart Tailor",
          "Deep Tailor with a completed and verified Career Profile",
          "Up to 300 confirmed Chrome Auto Apply submissions per billing cycle",
          "Maximum 20 confirmed Chrome Auto Apply submissions per day",
          "Unlimited deterministic résumé matching",
          "Unlimited deterministic Copilot form filling",
          "Up to 300 Copilot Generate/Refine with AI drafts per billing cycle",
          "Maximum 20 Copilot AI drafts per day",
          "Up to 50 manual Resume-tab AI previews per billing cycle",
          "Maximum 5 manual Resume-tab AI previews per day",
          "Pause, resume and cancel controls",
          "Priority processing",
          "Daily AI job search and matching",
          "Curated opportunities, unlimited alerts, direct links and analytics",
        ],
      },
    },
    interviewCopilotOnly: {
      tag: "Interview specialist",
      name: "Interview Assistant Only",
      description: "Complete live-interview assistance without purchasing Jobolar’s application-automation plans.",
      features: [
        "Starting at 60 minutes for one month",
        "$1.50 per 60 minutes after the 60% discount",
        "Select additional interview hours",
        "Manual and Smart Listen on Jobolar Web, the Chrome extension and Desktop",
        "Screen-share-invisible Desktop Interview Assistant",
        "Live transcription",
        "Real-time talking points and suggested answers",
        "Answer structure and interview insights",
        "Possible follow-up questions",
        "Text, microphone and waveform controls",
        "Purchased minutes reset according to the subscription billing cycle",
        "Does not include Email Auto Apply, Smart Apply or job-application automation",
      ],
    },
    faq: {
      eyebrow: "Clear by design",
      title: "Pricing and allowance details",
      items: [
        {
          id: "monthly-meaning",
          question: "What does “monthly” mean?",
          answer: "Monthly allowances follow your personal subscription billing cycle, not the first through last day of a calendar month. Quarterly and semiannual plans still reset usage on each monthly subscription anniversary.",
        },
        {
          id: "chrome-auto-apply-count",
          question: "What counts toward Chrome Auto Apply?",
          answer: "Chrome Auto Apply and Copilot AI drafts have separate allowances. Only confirmed submitted Chrome Auto Apply applications count. Failed runs, manual-help outcomes and review-only forms do not count.",
        },
        {
          id: "email-chrome-accounting",
          question: "Are Email Auto Apply and Chrome Auto Apply combined?",
          answer: "No. Email Auto Apply and Chrome Auto Apply retain separate accounting. Jobolar does not silently subtract an eligible email submission from your Chrome Auto Apply allowance.",
        },
        {
          id: "copilot-ai-credits",
          question: "What uses Copilot AI-draft credits?",
          answer: "Deterministic Copilot Fill is unlimited on Basic, Standard and Pro because it does not call the AI-generation workflow. Only Generate/Refine with AI consumes the separate Copilot AI-draft allowance.",
        },
        {
          id: "resume-modes",
          question: "How do résumé modes differ?",
          answer: "Standard automates only the unchanged Base Resume. Pro adds Smart Tailor and Deep Tailor; Deep Tailor requires a completed and verified Career Profile.",
        },
        {
          id: "email-auto-apply-availability",
          question: "How does Email Auto Apply availability work?",
          answer: "Email Auto Apply works only where Jobolar has supported job sources and a posting exposes a valid public employer or recruiter email. A supported country does not mean every posting can be submitted by email. Standard and Pro users can also use Chrome Smart Apply on supported job sites, including prominent options such as Indeed and LinkedIn; other available sites and application flows vary by country and posting.",
        },
      ],
    },
    countries: {
      title: "{count} Email Auto Apply countries",
      description: "Coverage depends on an eligible posting exposing a valid public email.",
      supportedLink: "{count} supported countries",
      regions: {
        Africa: "Africa",
        Europe: "Europe",
        "North America": "North America",
      },
    },
    labels: {
      planDetails: "See every plan detail",
      copilotDetails: "See all Interview Assistant details",
      choosePlan: "Choose plan",
      unlockCompleteExperience: "Unlock the complete experience",
      chooseCopilotOnly: "Choose Interview Assistant Only",
      loading: "Loading…",
      cancelNote: "Cancel anytime · No hidden fees",
      savePercent: "SAVE {percent}%",
      currentPlan: "Current plan",
      selectedPlan: "Selected plan",
      active: "Active",
      pricePerTerm: "/{term}",
      dueForTerm: "Due for the {term} term",
      selectHoursPerMonth: "Select hours per month",
      decreaseHoursAria: "Decrease hours",
      increaseHoursAria: "Increase hours",
      hoursInputAria: "Interview Assistant hours per month",
      hourPerMonth: "hour per month",
      hoursPerMonth: "hours per month",
      monthlyMinutesReset: "{minutes} minutes reset each billing month",
      prepaidHour: "{hours} prepaid hour across this term. No rollover.",
      prepaidHours: "{hours} prepaid hours across this term. No rollover.",
      currencyDisclaimer: "Prices are in USD. Taxes, currency conversion and payment-provider availability may vary by location.",
    },
  },
  fr: {
    page: {
      discountBadge: "{percent} % DE RÉDUCTION",
      eyebrow: "Tarifs de lancement",
      title: "Choisissez l’expérience Jobolar adaptée à votre prochaine étape.",
      description: "Commencez par un essai complet, puis conservez l’assistance en entretien et le parcours de candidature qui correspondent à votre façon de chercher.",
      promoLine: "Utilisez le code {code} · tous les prix affichés incluent déjà la réduction.",
      billingIntervalAria: "Période de facturation",
    },
    billing: {
      monthly: "Mensuel",
      quarterly: "Trimestriel",
      semiannual: "Semestriel",
      shortMonthly: "mois",
      shortQuarterly: "trim.",
      shortSemiannual: "sem.",
      termMonthly: "mensuelle",
      termQuarterly: "trimestrielle",
      termSemiannual: "semestrielle",
    },
    freeTrial: {
      badge: "Expérience complète pendant 7 jours",
      headline: "Découvrez l’expérience Jobolar complète — aucune carte requise.",
      features: [
        "Essai gratuit de 7 jours",
        "Aucune carte de paiement requise",
        "Un essai partagé entre Jobolar Web, l’extension Chrome et Interview Assistant",
        "Un essai par compte Google",
        "Jusqu’à 10 envois de candidature pendant l’essai, avec un maximum de 3 par jour",
        "Remplissage déterministe illimité des formulaires avec Copilot",
        "5 brouillons Générer/Affiner avec l’IA",
        "2 CV Smart Tailor",
        "2 CV Deep Tailor",
        "15 minutes d’Assistant d’entretien en direct",
        "15 minutes d’entretien simulé",
        "L’essai expire automatiquement",
      ],
      startCta: "Commencer mon essai gratuit",
      starting: "Démarrage…",
      activeUntil: "Essai actif jusqu’au {date}",
      unavailable: "L’activation de l’essai est temporairement indisponible",
      alreadyUsed: "Essai gratuit déjà utilisé",
      footnote: "Aucune carte de paiement · Expiration automatique",
    },
    plans: {
      basic: {
        name: "Basic",
        tag: "Un départ soigné",
        description: "Découvrez des offres pertinentes, remplissez les formulaires plus vite et préparez vos entretiens — sans envoi automatisé.",
        featuredFeatures: [
          "60 minutes d’Assistant d’entretien en direct par cycle de facturation",
          "90 minutes d’entretien simulé par cycle de facturation",
          "Écoute manuelle sur Jobolar Web pour les entretiens en direct",
          "Connexion de l’extension Chrome Jobolar",
          "Copilot Fill déterministe illimité via l’extension Chrome",
          "Recherche et matching d’emploi quotidiens par IA",
        ],
        features: [
          "Capture en temps réel des questions d’entretien, points clés et réponses suggérées",
          "Le remplissage déterministe ne consomme aucun crédit IA",
          "Offres d’emploi locales et internationales sélectionnées",
          "Alertes emploi illimitées",
          "Liens directs vers les offres envoyés par e-mail",
          "Tableau de bord d’analyse de la recherche d’emploi",
          "Passez à Standard ou Pro pour Smart Apply, Email Auto Apply, les brouillons IA, l’automatisation des CV et les envois planifiés",
        ],
      },
      standard: {
        name: "Standard",
        tag: "Le plus populaire · Meilleur rapport qualité-prix",
        description: "Laissez Jobolar envoyer les candidatures éligibles par e-mail avec votre CV de base inchangé, tandis que Smart Apply accélère les candidatures navigateur prises en charge.",
        featuredFeatures: [
          "150 minutes d’Assistant d’entretien en direct par cycle de facturation",
          "225 minutes d’entretien simulé par cycle de facturation",
          "Écoute manuelle sur Jobolar Web et dans l’extension Chrome",
          "Email Auto Apply avec votre CV de base inchangé",
          "Email Auto Apply disponible dans 49 pays pris en charge",
          "Smart Apply et Smart Form local au navigateur via l’extension Chrome",
          "Chrome Auto Apply planifié",
          "Jusqu’à 200 envois Chrome Auto Apply confirmés par cycle de facturation",
        ],
        features: [
          "Capture en temps réel des questions d’entretien, points clés et réponses suggérées",
          "Lettres de motivation générées par IA obligatoires pour les candidatures e-mail éligibles",
          "Réponses de candidature enregistrées",
          "Automatisation du CV de base",
          "Vérification avant envoi et envoi automatique pris en charge",
          "Génération obligatoire de la lettre de motivation",
          "Commandes de pause, reprise et annulation",
          "Maximum de 20 envois Chrome Auto Apply confirmés par jour",
          "Matching déterministe de CV illimité",
          "Remplissage déterministe Copilot illimité",
          "Jusqu’à 200 brouillons Copilot Générer/Affiner avec l’IA par cycle de facturation",
          "Maximum de 20 brouillons Copilot IA par jour",
          "Traitement prioritaire",
          "Recherche et matching d’emploi quotidiens par IA",
          "Offres sélectionnées, alertes illimitées, liens directs et analyses",
          "Standard utilise uniquement le CV de base inchangé",
          "Smart Tailor et Deep Tailor restent verrouillés",
        ],
      },
      pro: {
        name: "Pro",
        tag: "Expérience complète",
        description: "Postulez avec des CV et lettres adaptés par IA à chaque poste, débloquez toute l’automatisation Smart Apply et recevez des conseils IA discrets pendant les entretiens en direct.",
        featuredFeatures: [
          "300 minutes d’Assistant d’entretien en direct par cycle de facturation",
          "450 minutes d’entretien simulé par cycle de facturation",
          "Écoute manuelle et Smart Listen sur Jobolar Web, l’extension Chrome et Desktop",
          "Assistant d’entretien Desktop invisible au partage d’écran",
          "Email Auto Apply avec des CV adaptés par IA au poste",
          "Email Auto Apply disponible dans 49 pays pris en charge",
          "Tout ce qui est inclus dans Smart Apply Standard",
          "Chrome Auto Apply planifié",
        ],
        features: [
          "Capture des questions en temps réel, points clés suggérés, exemples de réponses, structure, analyses et relances possibles",
          "Lettres de motivation générées par IA obligatoires pour les candidatures e-mail éligibles",
          "CV de base",
          "Smart Tailor",
          "Deep Tailor avec un Profil de carrière complété et vérifié",
          "Jusqu’à 300 envois Chrome Auto Apply confirmés par cycle de facturation",
          "Maximum de 20 envois Chrome Auto Apply confirmés par jour",
          "Matching déterministe de CV illimité",
          "Remplissage déterministe Copilot illimité",
          "Jusqu’à 300 brouillons Copilot Générer/Affiner avec l’IA par cycle de facturation",
          "Maximum de 20 brouillons Copilot IA par jour",
          "Jusqu’à 50 aperçus IA manuels dans l’onglet CV par cycle de facturation",
          "Maximum de 5 aperçus IA manuels dans l’onglet CV par jour",
          "Commandes de pause, reprise et annulation",
          "Traitement prioritaire",
          "Recherche et matching d’emploi quotidiens par IA",
          "Offres sélectionnées, alertes illimitées, liens directs et analyses",
        ],
      },
    },
    interviewCopilotOnly: {
      tag: "Spécialiste des entretiens",
      name: "Assistant d’entretien uniquement",
      description: "Une assistance complète en entretien en direct sans acheter les forfaits d’automatisation des candidatures Jobolar.",
      features: [
        "À partir de 60 minutes pour un mois",
        "1,50 $ par tranche de 60 minutes après la réduction de 60 %",
        "Sélection d’heures d’entretien supplémentaires",
        "Écoute manuelle et Smart Listen sur Jobolar Web, l’extension Chrome et Desktop",
        "Assistant d’entretien Desktop invisible au partage d’écran",
        "Transcription en direct",
        "Points clés et réponses suggérées en temps réel",
        "Structure de réponse et analyses de l’entretien",
        "Questions de suivi possibles",
        "Commandes de texte, microphone et forme d’onde",
        "Les minutes achetées sont réinitialisées selon le cycle de facturation de l’abonnement",
        "N’inclut pas Email Auto Apply, Smart Apply ni l’automatisation des candidatures",
      ],
    },
    faq: {
      eyebrow: "Clair par conception",
      title: "Détails des tarifs et des quotas",
      items: [
        {
          id: "monthly-meaning",
          question: "Que signifie « mensuel » ?",
          answer: "Les quotas mensuels suivent votre cycle personnel de facturation, et non le premier et le dernier jour du mois civil. Les forfaits trimestriels et semestriels réinitialisent néanmoins l’usage à chaque date anniversaire mensuelle de l’abonnement.",
        },
        {
          id: "chrome-auto-apply-count",
          question: "Qu’est-ce qui compte dans Chrome Auto Apply ?",
          answer: "Chrome Auto Apply et les brouillons Copilot IA ont des quotas distincts. Seules les candidatures Chrome Auto Apply dont l’envoi est confirmé sont comptées. Les échecs, les résultats nécessitant une aide manuelle et les formulaires uniquement vérifiés ne le sont pas.",
        },
        {
          id: "email-chrome-accounting",
          question: "Email Auto Apply et Chrome Auto Apply sont-ils regroupés ?",
          answer: "Non. Email Auto Apply et Chrome Auto Apply conservent des décomptes séparés. Jobolar ne déduit pas silencieusement une candidature e-mail éligible de votre quota Chrome Auto Apply.",
        },
        {
          id: "copilot-ai-credits",
          question: "Qu’est-ce qui consomme les crédits de brouillons Copilot IA ?",
          answer: "Copilot Fill déterministe est illimité avec Basic, Standard et Pro, car il n’appelle pas le flux de génération IA. Seule l’action Générer/Affiner avec l’IA consomme le quota distinct de brouillons Copilot IA.",
        },
        {
          id: "resume-modes",
          question: "Quelle est la différence entre les modes de CV ?",
          answer: "Standard automatise uniquement le CV de base inchangé. Pro ajoute Smart Tailor et Deep Tailor ; Deep Tailor exige un Profil de carrière complété et vérifié.",
        },
        {
          id: "email-auto-apply-availability",
          question: "Comment fonctionne la disponibilité d’Email Auto Apply ?",
          answer: "Email Auto Apply fonctionne seulement lorsque Jobolar dispose de sources prises en charge et que l’offre publie une adresse valide d’employeur ou de recruteur. Un pays pris en charge ne signifie pas que chaque offre peut être envoyée par e-mail. Les utilisateurs Standard et Pro peuvent aussi utiliser Chrome Smart Apply sur les sites pris en charge, notamment Indeed et LinkedIn ; les autres sites et parcours disponibles varient selon le pays et l’offre.",
        },
      ],
    },
    countries: {
      title: "{count} pays Email Auto Apply",
      description: "La couverture dépend d’une offre éligible qui publie une adresse e-mail publique valide.",
      supportedLink: "{count} pays pris en charge",
      regions: {
        Africa: "Afrique",
        Europe: "Europe",
        "North America": "Amérique du Nord",
      },
    },
    labels: {
      planDetails: "Voir tous les détails du forfait",
      copilotDetails: "Voir tous les détails d’Interview Assistant",
      choosePlan: "Choisir le forfait",
      unlockCompleteExperience: "Débloquer l’expérience complète",
      chooseCopilotOnly: "Choisir l’Assistant d’entretien uniquement",
      loading: "Chargement…",
      cancelNote: "Annulez à tout moment · Aucun frais caché",
      savePercent: "ÉCONOMISEZ {percent} %",
      currentPlan: "Forfait actuel",
      selectedPlan: "Forfait sélectionné",
      active: "Actif",
      pricePerTerm: "/{term}",
      dueForTerm: "Dû pour la période {term}",
      selectHoursPerMonth: "Sélectionnez les heures par mois",
      decreaseHoursAria: "Réduire le nombre d’heures",
      increaseHoursAria: "Augmenter le nombre d’heures",
      hoursInputAria: "Heures d’Interview Assistant par mois",
      hourPerMonth: "heure par mois",
      hoursPerMonth: "heures par mois",
      monthlyMinutesReset: "{minutes} minutes réinitialisées chaque mois de facturation",
      prepaidHour: "{hours} heure prépayée sur cette période. Aucun report.",
      prepaidHours: "{hours} heures prépayées sur cette période. Aucun report.",
      currencyDisclaimer: "Les prix sont en USD. Les taxes, la conversion de devise et la disponibilité des prestataires de paiement peuvent varier selon le lieu.",
    },
  },
  de: {
    page: {
      discountBadge: "{percent} % RABATT",
      eyebrow: "Einführungspreise",
      title: "Wählen Sie das Jobolar-Erlebnis für Ihren nächsten Schritt.",
      description: "Beginnen Sie mit der vollständigen Testphase und behalten Sie anschließend die Interviewhilfe und den Bewerbungsablauf, die zu Ihrer Jobsuche passen.",
      promoLine: "Verwenden Sie den Code {code} · alle angezeigten Preise enthalten den Rabatt bereits.",
      billingIntervalAria: "Abrechnungszeitraum",
    },
    billing: {
      monthly: "Monatlich",
      quarterly: "Vierteljährlich",
      semiannual: "Halbjährlich",
      shortMonthly: "Mon.",
      shortQuarterly: "Quart.",
      shortSemiannual: "Halbj.",
      termMonthly: "monatlichen",
      termQuarterly: "vierteljährlichen",
      termSemiannual: "halbjährlichen",
    },
    freeTrial: {
      badge: "7 Tage vollständiges Erlebnis",
      headline: "Entdecken Sie das vollständige Jobolar-Erlebnis — keine Zahlungskarte erforderlich.",
      features: [
        "7-tägige kostenlose Testphase",
        "Keine Zahlungskarte erforderlich",
        "Eine gemeinsame Testphase für Jobolar Web, die Chrome-Erweiterung und Interview Assistant",
        "Eine Testphase pro Google-Konto",
        "Bis zu 10 Bewerbungseinreichungen während der Testphase, höchstens 3 pro Tag",
        "Unbegrenztes deterministisches Ausfüllen von Formularen mit Copilot",
        "5 Entwürfe mit Generieren/Verfeinern durch KI",
        "2 Smart-Tailor-Lebensläufe",
        "2 Deep-Tailor-Lebensläufe",
        "15 Minuten Live Interview Assistant",
        "15 Minuten Probeinterview",
        "Die Testphase läuft automatisch ab",
      ],
      startCta: "Kostenlose Testphase starten",
      starting: "Wird gestartet…",
      activeUntil: "Testphase aktiv bis {date}",
      unavailable: "Die Aktivierung der Testphase ist vorübergehend nicht verfügbar",
      alreadyUsed: "Kostenlose Testphase bereits genutzt",
      footnote: "Keine Zahlungskarte · Läuft automatisch ab",
    },
    plans: {
      basic: {
        name: "Basic",
        tag: "Ein gelungener Start",
        description: "Entdecken Sie passende Stellen, füllen Sie Bewerbungsformulare schneller aus und bereiten Sie sich auf Interviews vor — ohne automatische Einreichungen.",
        featuredFeatures: [
          "60 Minuten Live Interview Assistant pro Abrechnungszyklus",
          "90 Minuten Probeinterview pro Abrechnungszyklus",
          "Manuelles Zuhören auf Jobolar Web für Live-Interviews",
          "Verbindung mit der Jobolar Chrome-Erweiterung",
          "Unbegrenztes deterministisches Copilot Fill über die Chrome-Erweiterung",
          "Tägliche KI-Jobsuche und tägliches Matching",
        ],
        features: [
          "Echtzeit-Erfassung von Interviewfragen, Stichpunkte und Antwortvorschläge",
          "Deterministisches Ausfüllen verbraucht keine KI-Guthaben",
          "Kuratierte lokale und internationale Stellenangebote",
          "Unbegrenzte Job-Alerts",
          "Direkte Stellenlinks per E-Mail",
          "Analyse-Dashboard für die Jobsuche",
          "Wechseln Sie für Smart Apply, Email Auto Apply, KI-Bewerbungsentwürfe, Lebenslaufautomatisierung und geplante Einreichungen zu Standard oder Pro",
        ],
      },
      standard: {
        name: "Standard",
        tag: "Am beliebtesten · Bestes Preis-Leistungs-Verhältnis",
        description: "Lassen Sie Jobolar geeignete E-Mail-Bewerbungen mit Ihrem unveränderten Basislebenslauf senden, während Smart Apply unterstützte Browserbewerbungen beschleunigt.",
        featuredFeatures: [
          "150 Minuten Live Interview Assistant pro Abrechnungszyklus",
          "225 Minuten Probeinterview pro Abrechnungszyklus",
          "Manuelles Zuhören auf Jobolar Web und in der Chrome-Erweiterung",
          "Email Auto Apply mit Ihrem unveränderten Basislebenslauf",
          "Email Auto Apply in 49 unterstützten Ländern",
          "Smart Apply und browserlokales Smart Form über die Chrome-Erweiterung",
          "Geplantes Chrome Auto Apply",
          "Bis zu 200 bestätigte Chrome-Auto-Apply-Einreichungen pro Abrechnungszyklus",
        ],
        features: [
          "Echtzeit-Erfassung von Interviewfragen, Stichpunkte und Antwortvorschläge",
          "Erforderliche KI-generierte Anschreiben für geeignete E-Mail-Bewerbungen",
          "Gespeicherte Bewerbungsantworten",
          "Automatisierung des Basislebenslaufs",
          "Prüfung vor dem Absenden und unterstützte automatische Einreichung",
          "Erforderliche Anschreibenerstellung",
          "Steuerungen zum Pausieren, Fortsetzen und Abbrechen",
          "Höchstens 20 bestätigte Chrome-Auto-Apply-Einreichungen pro Tag",
          "Unbegrenztes deterministisches Lebenslauf-Matching",
          "Unbegrenztes deterministisches Ausfüllen mit Copilot",
          "Bis zu 200 Copilot-Entwürfe mit Generieren/Verfeinern durch KI pro Abrechnungszyklus",
          "Höchstens 20 Copilot-KI-Entwürfe pro Tag",
          "Bevorzugte Verarbeitung",
          "Tägliche KI-Jobsuche und tägliches Matching",
          "Kuratierte Chancen, unbegrenzte Alerts, Direktlinks und Analysen",
          "Standard verwendet nur den unveränderten Basislebenslauf",
          "Smart Tailor und Deep Tailor bleiben gesperrt",
        ],
      },
      pro: {
        name: "Pro",
        tag: "Vollständiges Erlebnis",
        description: "Bewerben Sie sich mit stellenspezifischen KI-angepassten Lebensläufen und Anschreiben, nutzen Sie die vollständige Smart-Apply-Automatisierung und erhalten Sie diskrete KI-Hinweise in Live-Interviews.",
        featuredFeatures: [
          "300 Minuten Live Interview Assistant pro Abrechnungszyklus",
          "450 Minuten Probeinterview pro Abrechnungszyklus",
          "Manuelles und Smart Listen auf Jobolar Web, in der Chrome-Erweiterung und auf Desktop",
          "Bei Bildschirmfreigabe unsichtbarer Desktop Interview Assistant",
          "Email Auto Apply mit stellenspezifischen KI-angepassten Lebensläufen",
          "Email Auto Apply in 49 unterstützten Ländern",
          "Alles aus Standard Smart Apply",
          "Geplantes Chrome Auto Apply",
        ],
        features: [
          "Echtzeit-Erfassung von Fragen, vorgeschlagene Stichpunkte, Beispielantworten, Antwortstruktur, Einblicke und mögliche Rückfragen",
          "Erforderliche KI-generierte Anschreiben für geeignete E-Mail-Bewerbungen",
          "Basislebenslauf",
          "Smart Tailor",
          "Deep Tailor mit einem ausgefüllten und verifizierten Karriereprofil",
          "Bis zu 300 bestätigte Chrome-Auto-Apply-Einreichungen pro Abrechnungszyklus",
          "Höchstens 20 bestätigte Chrome-Auto-Apply-Einreichungen pro Tag",
          "Unbegrenztes deterministisches Lebenslauf-Matching",
          "Unbegrenztes deterministisches Ausfüllen mit Copilot",
          "Bis zu 300 Copilot-Entwürfe mit Generieren/Verfeinern durch KI pro Abrechnungszyklus",
          "Höchstens 20 Copilot-KI-Entwürfe pro Tag",
          "Bis zu 50 manuelle KI-Vorschauen im Lebenslauf-Tab pro Abrechnungszyklus",
          "Höchstens 5 manuelle KI-Vorschauen im Lebenslauf-Tab pro Tag",
          "Steuerungen zum Pausieren, Fortsetzen und Abbrechen",
          "Bevorzugte Verarbeitung",
          "Tägliche KI-Jobsuche und tägliches Matching",
          "Kuratierte Chancen, unbegrenzte Alerts, Direktlinks und Analysen",
        ],
      },
    },
    interviewCopilotOnly: {
      tag: "Interview-Spezialist",
      name: "Nur Interview Assistant",
      description: "Vollständige Unterstützung bei Live-Interviews, ohne Jobolars Tarife für Bewerbungsautomatisierung zu kaufen.",
      features: [
        "Ab 60 Minuten für einen Monat",
        "1,50 $ je 60 Minuten nach 60 % Rabatt",
        "Zusätzliche Interviewstunden auswählen",
        "Manuelles und Smart Listen auf Jobolar Web, in der Chrome-Erweiterung und auf Desktop",
        "Bei Bildschirmfreigabe unsichtbarer Desktop Interview Assistant",
        "Live-Transkription",
        "Stichpunkte und Antwortvorschläge in Echtzeit",
        "Antwortstruktur und Intervieweinblicke",
        "Mögliche Rückfragen",
        "Steuerungen für Text, Mikrofon und Wellenform",
        "Gekaufte Minuten werden gemäß dem Abrechnungszyklus des Abonnements zurückgesetzt",
        "Enthält weder Email Auto Apply noch Smart Apply oder Bewerbungsautomatisierung",
      ],
    },
    faq: {
      eyebrow: "Bewusst klar",
      title: "Preis- und Kontingentdetails",
      items: [
        {
          id: "monthly-meaning",
          question: "Was bedeutet „monatlich“?",
          answer: "Monatliche Kontingente folgen Ihrem persönlichen Abrechnungszyklus und nicht dem ersten bis letzten Tag eines Kalendermonats. Auch bei vierteljährlichen und halbjährlichen Tarifen wird die Nutzung an jedem monatlichen Abonnementstichtag zurückgesetzt.",
        },
        {
          id: "chrome-auto-apply-count",
          question: "Was wird auf Chrome Auto Apply angerechnet?",
          answer: "Chrome Auto Apply und Copilot-KI-Entwürfe haben getrennte Kontingente. Nur bestätigte, abgesendete Chrome-Auto-Apply-Bewerbungen werden gezählt. Fehlgeschlagene Abläufe, Ergebnisse mit manueller Hilfe und reine Prüfformulare werden nicht gezählt.",
        },
        {
          id: "email-chrome-accounting",
          question: "Werden Email Auto Apply und Chrome Auto Apply zusammengezählt?",
          answer: "Nein. Email Auto Apply und Chrome Auto Apply werden getrennt erfasst. Jobolar zieht eine geeignete E-Mail-Bewerbung nicht unbemerkt von Ihrem Chrome-Auto-Apply-Kontingent ab.",
        },
        {
          id: "copilot-ai-credits",
          question: "Was verbraucht Guthaben für Copilot-KI-Entwürfe?",
          answer: "Deterministisches Copilot Fill ist bei Basic, Standard und Pro unbegrenzt, da es den KI-Generierungsablauf nicht aufruft. Nur Generieren/Verfeinern mit KI verbraucht das separate Kontingent für Copilot-KI-Entwürfe.",
        },
        {
          id: "resume-modes",
          question: "Wie unterscheiden sich die Lebenslaufmodi?",
          answer: "Standard automatisiert nur den unveränderten Basislebenslauf. Pro ergänzt Smart Tailor und Deep Tailor; Deep Tailor erfordert ein ausgefülltes und verifiziertes Karriereprofil.",
        },
        {
          id: "email-auto-apply-availability",
          question: "Wie funktioniert die Verfügbarkeit von Email Auto Apply?",
          answer: "Email Auto Apply funktioniert nur, wenn Jobolar unterstützte Stellenquellen hat und eine Anzeige eine gültige öffentliche E-Mail-Adresse des Arbeitgebers oder Recruiters veröffentlicht. Ein unterstütztes Land bedeutet nicht, dass jede Anzeige per E-Mail eingereicht werden kann. Standard- und Pro-Nutzer können Chrome Smart Apply auch auf unterstützten Jobseiten wie Indeed und LinkedIn verwenden; andere verfügbare Seiten und Abläufe variieren je nach Land und Anzeige.",
        },
      ],
    },
    countries: {
      title: "{count} Email-Auto-Apply-Länder",
      description: "Die Abdeckung hängt von einer geeigneten Anzeige mit einer gültigen öffentlichen E-Mail-Adresse ab.",
      supportedLink: "{count} unterstützte Länder",
      regions: {
        Africa: "Afrika",
        Europe: "Europa",
        "North America": "Nordamerika",
      },
    },
    labels: {
      planDetails: "Alle Tarifdetails anzeigen",
      copilotDetails: "Alle Interview-Assistant-Details anzeigen",
      choosePlan: "Tarif wählen",
      unlockCompleteExperience: "Vollständiges Erlebnis freischalten",
      chooseCopilotOnly: "Nur Interview Assistant wählen",
      loading: "Wird geladen…",
      cancelNote: "Jederzeit kündbar · Keine versteckten Gebühren",
      savePercent: "{percent} % SPAREN",
      currentPlan: "Aktueller Tarif",
      selectedPlan: "Ausgewählter Tarif",
      active: "Aktiv",
      pricePerTerm: "/{term}",
      dueForTerm: "Fällig für den {term} Zeitraum",
      selectHoursPerMonth: "Stunden pro Monat auswählen",
      decreaseHoursAria: "Stunden verringern",
      increaseHoursAria: "Stunden erhöhen",
      hoursInputAria: "Interview-Assistant-Stunden pro Monat",
      hourPerMonth: "Stunde pro Monat",
      hoursPerMonth: "Stunden pro Monat",
      monthlyMinutesReset: "{minutes} Minuten werden in jedem Abrechnungsmonat zurückgesetzt",
      prepaidHour: "{hours} vorausbezahlte Stunde in diesem Zeitraum. Keine Übertragung.",
      prepaidHours: "{hours} vorausbezahlte Stunden in diesem Zeitraum. Keine Übertragung.",
      currencyDisclaimer: "Die Preise sind in USD angegeben. Steuern, Währungsumrechnung und die Verfügbarkeit von Zahlungsanbietern können je nach Standort variieren.",
    },
  },
  es: {
    page: {
      discountBadge: "{percent} % DE DESCUENTO",
      eyebrow: "Precios de lanzamiento",
      title: "Elige la experiencia de Jobolar creada para tu próximo paso.",
      description: "Empieza con una prueba completa y conserva después la ayuda para entrevistas y el flujo de solicitudes que mejor se adapten a tu búsqueda.",
      promoLine: "Usa el código {code} · todos los precios mostrados ya incluyen el descuento.",
      billingIntervalAria: "Intervalo de facturación",
    },
    billing: {
      monthly: "Mensual",
      quarterly: "Trimestral",
      semiannual: "Semestral",
      shortMonthly: "mes",
      shortQuarterly: "trim.",
      shortSemiannual: "sem.",
      termMonthly: "mensual",
      termQuarterly: "trimestral",
      termSemiannual: "semestral",
    },
    freeTrial: {
      badge: "Experiencia completa durante 7 días",
      headline: "Explora la experiencia completa de Jobolar sin tarjeta.",
      features: [
        "Prueba gratuita de 7 días",
        "No se requiere tarjeta de pago",
        "Una prueba compartida entre Jobolar Web, la extensión de Chrome e Interview Assistant",
        "Una prueba por cuenta de Google",
        "Hasta 10 envíos de solicitudes durante la prueba, con un máximo de 3 al día",
        "Llenado determinista ilimitado de formularios con Copilot",
        "5 borradores de Generar/Refinar con IA",
        "2 currículums Smart Tailor",
        "2 currículums Deep Tailor",
        "15 minutos de Asistente de entrevista en vivo",
        "15 minutos de entrevista simulada",
        "La prueba caduca automáticamente",
      ],
      startCta: "Iniciar mi prueba gratuita",
      starting: "Iniciando…",
      activeUntil: "Prueba activa hasta el {date}",
      unavailable: "La activación de la prueba no está disponible temporalmente",
      alreadyUsed: "La prueba gratuita ya se utilizó",
      footnote: "Sin tarjeta de pago · Caduca automáticamente",
    },
    plans: {
      basic: {
        name: "Basic",
        tag: "Un comienzo pulido",
        description: "Descubre oportunidades relevantes, completa formularios más rápido y prepárate para entrevistas, sin envíos automatizados.",
        featuredFeatures: [
          "60 minutos de Asistente de entrevista en vivo por ciclo de facturación",
          "90 minutos de entrevista simulada por ciclo de facturación",
          "Escucha manual en Jobolar Web para entrevistas en vivo",
          "Conexión de la extensión de Chrome de Jobolar",
          "Copilot Fill determinista ilimitado mediante la extensión de Chrome",
          "Búsqueda y coincidencia diaria de empleos con IA",
        ],
        features: [
          "Captura en tiempo real de preguntas de entrevista, puntos clave y respuestas sugeridas",
          "El llenado determinista no consume créditos de IA",
          "Oportunidades de empleo locales e internacionales seleccionadas",
          "Alertas de empleo ilimitadas",
          "Enlaces directos a empleos enviados por correo",
          "Panel de análisis de búsqueda de empleo",
          "Mejora a Standard o Pro para Smart Apply, Email Auto Apply, borradores de solicitud con IA, automatización de currículums y envíos programados",
        ],
      },
      standard: {
        name: "Standard",
        tag: "Más popular · Mejor valor",
        description: "Deja que Jobolar envíe solicitudes por correo elegibles con tu Currículum Base sin cambios, mientras Smart Apply acelera las solicitudes compatibles del navegador.",
        featuredFeatures: [
          "150 minutos de Asistente de entrevista en vivo por ciclo de facturación",
          "225 minutos de entrevista simulada por ciclo de facturación",
          "Escucha manual en Jobolar Web y la extensión de Chrome",
          "Email Auto Apply con tu Currículum Base sin cambios",
          "Email Auto Apply disponible en 49 países compatibles",
          "Smart Apply y Smart Form local del navegador mediante la extensión de Chrome",
          "Chrome Auto Apply programado",
          "Hasta 200 envíos confirmados de Chrome Auto Apply por ciclo de facturación",
        ],
        features: [
          "Captura en tiempo real de preguntas de entrevista, puntos clave y respuestas sugeridas",
          "Cartas de presentación generadas con IA obligatorias para solicitudes por correo elegibles",
          "Respuestas de solicitud guardadas",
          "Automatización del Currículum Base",
          "Revisión antes de enviar y envío automático compatible",
          "Generación obligatoria de carta de presentación",
          "Controles de pausa, reanudación y cancelación",
          "Máximo de 20 envíos confirmados de Chrome Auto Apply al día",
          "Coincidencia determinista de currículum ilimitada",
          "Llenado determinista ilimitado con Copilot",
          "Hasta 200 borradores Copilot de Generar/Refinar con IA por ciclo de facturación",
          "Máximo de 20 borradores Copilot con IA al día",
          "Procesamiento prioritario",
          "Búsqueda y coincidencia diaria de empleos con IA",
          "Oportunidades seleccionadas, alertas ilimitadas, enlaces directos y análisis",
          "Standard usa únicamente el Currículum Base sin cambios",
          "Smart Tailor y Deep Tailor permanecen bloqueados",
        ],
      },
      pro: {
        name: "Pro",
        tag: "Experiencia completa",
        description: "Postula con currículums y cartas adaptados por IA a cada empleo, desbloquea toda la automatización de Smart Apply y recibe orientación discreta de IA en entrevistas en vivo.",
        featuredFeatures: [
          "300 minutos de Asistente de entrevista en vivo por ciclo de facturación",
          "450 minutos de entrevista simulada por ciclo de facturación",
          "Escucha manual y Smart Listen en Jobolar Web, la extensión de Chrome y Desktop",
          "Asistente de entrevistas Desktop invisible al compartir pantalla",
          "Email Auto Apply con currículums adaptados por IA a cada empleo",
          "Email Auto Apply disponible en 49 países compatibles",
          "Todo lo incluido en Smart Apply de Standard",
          "Chrome Auto Apply programado",
        ],
        features: [
          "Captura de preguntas en tiempo real, puntos clave sugeridos, respuestas de ejemplo, estructura, información y posibles preguntas de seguimiento",
          "Cartas de presentación generadas con IA obligatorias para solicitudes por correo elegibles",
          "Currículum Base",
          "Smart Tailor",
          "Deep Tailor con un Perfil Profesional completado y verificado",
          "Hasta 300 envíos confirmados de Chrome Auto Apply por ciclo de facturación",
          "Máximo de 20 envíos confirmados de Chrome Auto Apply al día",
          "Coincidencia determinista de currículum ilimitada",
          "Llenado determinista ilimitado con Copilot",
          "Hasta 300 borradores Copilot de Generar/Refinar con IA por ciclo de facturación",
          "Máximo de 20 borradores Copilot con IA al día",
          "Hasta 50 vistas previas manuales con IA en la pestaña Currículum por ciclo de facturación",
          "Máximo de 5 vistas previas manuales con IA en la pestaña Currículum al día",
          "Controles de pausa, reanudación y cancelación",
          "Procesamiento prioritario",
          "Búsqueda y coincidencia diaria de empleos con IA",
          "Oportunidades seleccionadas, alertas ilimitadas, enlaces directos y análisis",
        ],
      },
    },
    interviewCopilotOnly: {
      tag: "Especialista en entrevistas",
      name: "Solo Asistente de entrevistas",
      description: "Asistencia completa para entrevistas en vivo sin comprar los planes de automatización de solicitudes de Jobolar.",
      features: [
        "Desde 60 minutos durante un mes",
        "1,50 $ por 60 minutos después del 60 % de descuento",
        "Selección de horas de entrevista adicionales",
        "Escucha manual y Smart Listen en Jobolar Web, la extensión de Chrome y Desktop",
        "Asistente de entrevistas Desktop invisible al compartir pantalla",
        "Transcripción en vivo",
        "Puntos clave y respuestas sugeridas en tiempo real",
        "Estructura de respuesta e información de la entrevista",
        "Posibles preguntas de seguimiento",
        "Controles de texto, micrófono y forma de onda",
        "Los minutos comprados se reinician según el ciclo de facturación de la suscripción",
        "No incluye Email Auto Apply, Smart Apply ni automatización de solicitudes",
      ],
    },
    faq: {
      eyebrow: "Claridad desde el diseño",
      title: "Detalles de precios y límites",
      items: [
        {
          id: "monthly-meaning",
          question: "¿Qué significa «mensual»?",
          answer: "Los límites mensuales siguen tu ciclo personal de facturación, no el primer y último día de un mes natural. Los planes trimestrales y semestrales siguen reiniciando el uso en cada aniversario mensual de la suscripción.",
        },
        {
          id: "chrome-auto-apply-count",
          question: "¿Qué cuenta para Chrome Auto Apply?",
          answer: "Chrome Auto Apply y los borradores Copilot con IA tienen límites separados. Solo cuentan las solicitudes de Chrome Auto Apply cuyo envío está confirmado. No cuentan los intentos fallidos, los resultados que requieren ayuda manual ni los formularios solo revisados.",
        },
        {
          id: "email-chrome-accounting",
          question: "¿Se combinan Email Auto Apply y Chrome Auto Apply?",
          answer: "No. Email Auto Apply y Chrome Auto Apply mantienen recuentos separados. Jobolar no resta silenciosamente un envío por correo elegible de tu límite de Chrome Auto Apply.",
        },
        {
          id: "copilot-ai-credits",
          question: "¿Qué consume créditos de borradores Copilot con IA?",
          answer: "Copilot Fill determinista es ilimitado en Basic, Standard y Pro porque no llama al flujo de generación con IA. Solo Generar/Refinar con IA consume el límite independiente de borradores Copilot con IA.",
        },
        {
          id: "resume-modes",
          question: "¿En qué se diferencian los modos de currículum?",
          answer: "Standard automatiza únicamente el Currículum Base sin cambios. Pro añade Smart Tailor y Deep Tailor; Deep Tailor requiere un Perfil Profesional completado y verificado.",
        },
        {
          id: "email-auto-apply-availability",
          question: "¿Cómo funciona la disponibilidad de Email Auto Apply?",
          answer: "Email Auto Apply funciona solo donde Jobolar tiene fuentes compatibles y la oferta publica un correo válido del empleador o reclutador. Que un país sea compatible no significa que todas las ofertas puedan enviarse por correo. Los usuarios Standard y Pro también pueden usar Chrome Smart Apply en sitios compatibles, incluidas opciones destacadas como Indeed y LinkedIn; otros sitios y flujos disponibles varían según el país y la oferta.",
        },
      ],
    },
    countries: {
      title: "{count} países de Email Auto Apply",
      description: "La cobertura depende de una oferta elegible que publique un correo público válido.",
      supportedLink: "{count} países compatibles",
      regions: {
        Africa: "África",
        Europe: "Europa",
        "North America": "América del Norte",
      },
    },
    labels: {
      planDetails: "Ver todos los detalles del plan",
      copilotDetails: "Ver todos los detalles de Interview Assistant",
      choosePlan: "Elegir plan",
      unlockCompleteExperience: "Desbloquear la experiencia completa",
      chooseCopilotOnly: "Elegir Solo Asistente de entrevistas",
      loading: "Cargando…",
      cancelNote: "Cancela cuando quieras · Sin cargos ocultos",
      savePercent: "AHORRA {percent} %",
      currentPlan: "Plan actual",
      selectedPlan: "Plan seleccionado",
      active: "Activo",
      pricePerTerm: "/{term}",
      dueForTerm: "Importe del periodo {term}",
      selectHoursPerMonth: "Selecciona horas al mes",
      decreaseHoursAria: "Reducir horas",
      increaseHoursAria: "Aumentar horas",
      hoursInputAria: "Horas de Interview Assistant al mes",
      hourPerMonth: "hora al mes",
      hoursPerMonth: "horas al mes",
      monthlyMinutesReset: "{minutes} minutos se reinician cada mes de facturación",
      prepaidHour: "{hours} hora prepagada durante este periodo. Sin acumulación.",
      prepaidHours: "{hours} horas prepagadas durante este periodo. Sin acumulación.",
      currencyDisclaimer: "Los precios se indican en USD. Los impuestos, la conversión de moneda y la disponibilidad del proveedor de pago pueden variar según la ubicación.",
    },
  },
};

export const PRICING_PREMIUM_LOCALES = Object.freeze([
  "en",
  "fr",
  "de",
  "es",
]);

export function getPricingPremiumContent(locale) {
  const language = String(locale || "en").toLowerCase().split("-")[0];
  return CONTENT[language] || CONTENT.en;
}

export function formatPricingPremiumText(template, values = {}) {
  return String(template ?? "").replace(
    /\{([A-Za-z0-9_]+)\}/g,
    (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key)
        ? String(values[key])
        : match
  );
}

export default CONTENT;
